import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";

import { client } from "~/service/api.ts";
import { ServerUnhealthy } from "~/ui/page/ServerUnhealthy.tsx";

interface ServerHealthContextType {
  checkHealth: () => Promise<void>;
  isChecking: boolean;
  isHealthy: boolean | null;
}

const ServerHealthContext = createContext<null | ServerHealthContextType>(null);

export const useServerHealth = () => {
  const context = useContext(ServerHealthContext);
  if (!context) {
    throw new Error(
      "useServerHealth must be used within a ServerHealthProvider",
    );
  }
  return context;
};

const closeSplash = () => {
  const splashElement = document.getElementById("splash");
  if (splashElement) {
    splashElement.classList.add("opacity-0", "pointer-events-none");
    splashElement.ariaHidden = "true";
  }
};

export const ServerHealthProvider = ({ children }: { children: ReactNode }) => {
  const [isHealthy, setIsHealthy] = useState<boolean | null>(null);
  const [isChecking, setIsChecking] = useState(false);

  const checkHealth = useCallback(async () => {
    setIsChecking(true);
    try {
      const res = await client.get("util/health-check");
      if (res.status === 200) {
        setIsHealthy(true);
      } else {
        setIsHealthy(false);
        closeSplash();
      }
    } catch {
      setIsHealthy(false);
      closeSplash();
    } finally {
      setIsChecking(false);
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      void checkHealth();
    }, 0);
    return () => {
      clearTimeout(timer);
    };
  }, [checkHealth]);

  useEffect(() => {
    const handleUnhealthy = () => {
      setIsHealthy(false);
      closeSplash();
    };

    window.addEventListener("server-unhealthy", handleUnhealthy);
    return () => {
      window.removeEventListener("server-unhealthy", handleUnhealthy);
    };
  }, []);

  if (isHealthy === false) {
    return (
      <ServerUnhealthy
        isChecking={isChecking}
        onRetry={() => {
          void checkHealth();
        }}
      />
    );
  }

  // Render children (or null if we are doing the initial health check)
  if (isHealthy === null) {
    return null;
  }

  return (
    <ServerHealthContext.Provider
      value={{
        checkHealth,
        isChecking,
        isHealthy,
      }}
    >
      {children}
    </ServerHealthContext.Provider>
  );
};
